import _ from "lodash";
import moment from "moment";
import request from "superagent";

import env from "../constants/env";
import PlayerPaths from "../constants/paths/playerPaths";
import * as awsS3 from "../persistence/s3";
import * as inventoryActions from "./inventoryActions";

import { fetchInventory, fetchDrive } from "./portalActions";
import * as navActions from "./navActions";
import * as algolia from "../search/algolia";
import { showError, showInfo, showWarningToast, showYesNo } from "./alertActions";
import state from "../state/state";
import { generateUUID, getLs, getUtcTimestamp, setLs } from "../utils/utils";
import browserHistory from "../history";
import * as userActions from "./user";
import { refreshCredentials } from "../utils/securityUtil";
import { initializeDrive } from "./drive";
import { addActivityInstance, closeDetailedStatusModal } from "./actions";
import { markAssignmentSubmitted, getAssignmentScheduleForCurrentUser, getHealthStreamAssignmentScheduleForCurrentUser, refreshHealthStreamLearningForCurrentUser } from "./assignmentActions";
import { initializeWorkflows, initializeWorkflowParticipants } from "./workflowActions";
import { updateActivityWorkflowWorkItemProgress } from "./notificationActions";
import { getOrgId } from "./orgsActions";
import { sendLearningEvent, sendLearningEventForAmbifiActivity } from "./healthStream";
import naturalCompare from "string-natural-compare";
import { logInAppConsole, logWebLoginSequence } from "../utils/webLoginSequenceLogger";
import { isProjectOpsEnabledForOrg } from "../utils/projectOpsUtils";
import { advanceIchProjectForMilestones, stampIchInstanceForSave } from "./ichProjectOpsActions";
import { getFeatureFlagValue, isHealthStreamFutureEnabled } from "../utils/featureFlags";
import { HCA_HEALTHSTREAM_ORG_ID, resolveCompletedActivityInstanceId } from "../utils/healthStreamLearning";
import {
	buildFreshInstanceAuthorization,
	buildActivityInstanceLaunch,
	buildHistoryResumeFilters,
	canResumeAnyHistoryInCurrentOrg,
	isExactFreshInstanceAuthorization,
	resolveActivityResume,
	shouldDelegateMissingExactResumeToPlayer
} from "../utils/playerResumeUtils";

function emitMobilePerfSample(label, durationMs, meta = {}) {
	if (typeof window === "undefined" || !window.ReactNativeWebView || typeof window.ReactNativeWebView.postMessage !== "function") {
		return;
	}

	try {
		window.ReactNativeWebView.postMessage(JSON.stringify({
			method: "perfSample",
			sample: {
				source: "cloud-webview",
				label,
				durationMs,
				meta,
				timestamp: Date.now()
			}
		}));
	} catch (err) {
		console.error("Unable to emit mobile perf sample", err);
	}
}

function startPerfTimer(label, meta = null) {
	const start = Date.now();
	if (meta) {
		console.log(`[perf] ${label}: start`, meta);
	} else {
		console.log(`[perf] ${label}: start`);
	}

	return (extraMeta = null) => {
		const durationMs = Date.now() - start;
		const mergedMeta = _.assign({}, meta || {}, extraMeta || {});
		if (meta || extraMeta) {
			console.log(`[perf] ${label}: ${durationMs}ms`, mergedMeta);
		} else {
			console.log(`[perf] ${label}: ${durationMs}ms`);
		}
		emitMobilePerfSample(label, durationMs, mergedMeta);
		return durationMs;
	};
}

const reloadActivitiesPromisesByKey = {};
const reloadActivitiesFastPromisesByKey = {};
const RECENT_ACTIVITY_CACHE_STORAGE_SUFFIX = "recentActivityCache";
const RECENT_ACTIVITY_CACHE_MAX_ENTRIES = 50;
const RECENT_ACTIVITY_SNAPSHOT_FIELDS = [
	"id",
	"name",
	"description",
	"privacy",
	"orgPrivacy",
	"visible",
	"genre",
	"heroImage",
	"heroImageUrl",
	"trackData",
	"newDoc",
	"owner",
	"sharedWithType",
	"publicationVersion",
	"revision",
	"revisionTs",
	"publicationRevision",
	"publicationReleaseNotes"
];
const PLAYER_LAUNCH_LOOP_STORAGE_KEY = "__ambifiPlayerLaunchLoopV1";
const PLAYER_LAUNCH_LOOP_WINDOW_MS = 15000;
const PLAYER_LAUNCH_LOOP_ALERT_COOLDOWN_MS = 15000;
const PLAYER_LAUNCH_LOOP_DISTINCT_INSTANCE_THRESHOLD = 3;
const PLAYER_LAUNCH_SERVER_DIAGNOSTIC_COOLDOWN_MS = 30000;
let reloadActivitiesRequestSequence = 0;
let reloadActivitiesFastRequestSequence = 0;

function getCurrentLaunchPath() {
	if (typeof window === "undefined" || !window.location) {
		return "";
	}

	return `${_.toString(window.location.pathname || "")}${_.toString(window.location.search || "")}${_.toString(window.location.hash || "")}`;
}

function getLaunchUserMeta(user = state.get(["user"]) || {}) {
	const upn = _.toString(user["custom:upn"] || "").trim();
	return {
		username: _.toString(user.username || "").trim(),
		identityId: _.toString(user.identityId || "").trim(),
		hcaPrincipal: upn ? upn.split("@")[0] : ""
	};
}

function getLaunchQueryParam(name) {
	if (typeof window === "undefined" || !window.location) {
		return "";
	}
	try {
		return _.toString(new URLSearchParams(window.location.search || "").get(name) || "").trim();
	} catch (_err) {
		return "";
	}
}

function buildCloudPlayerLaunchProvenance({
	activityId,
	instanceId,
	intent,
	origin,
	source,
	launchId = generateUUID()
} = {}) {
	return buildActivityInstanceLaunch({
		launchId,
		intent,
		origin,
		source,
		activityId,
		activityInstanceId: instanceId,
		cloudVersion: state.get(["version"]) || {},
		client: typeof window !== "undefined" && window.ReactNativeWebView ? "mobileWebView" : "web",
		appApiVersion: getLaunchQueryParam("app-api-version"),
		nativeAppLaunchId: getLaunchQueryParam("nativeAppLaunchId")
	});
}

function getPlayerLaunchDiagnosticEndpoint() {
	const endpoint = _.get(env, ["apiGateway", "endpoints", "services", "ambifi", "event"], "/services/ambifi/event");
	const baseUrl = _.toString(_.get(env, ["apiGateway", "baseUrl"], "")).replace(/\/$/, "");
	return baseUrl && endpoint ? `${baseUrl}${endpoint}` : "";
}

function sanitizePlayerLaunchDiagnosticMeta(meta = {}) {
	return _.pick(meta || {}, [
		"activityId",
		"instanceId",
		"initialItemId",
		"source",
		"currentPath",
		"origin",
		"userEntryPoint",
		"selectedActivityId",
		"selectedActivityInstanceId",
		"username",
		"identityId",
		"hcaPrincipal",
		"sessionCheck",
		"assignmentMode",
		"hasUrl",
		"providedInstanceId",
		"generatedInstanceId",
		"distinctInstanceIds",
		"eventCount",
		"windowMs"
	]);
}

function postPlayerLaunchDiagnostic(diagnosticType, meta = {}) {
	if (typeof window === "undefined" || !window.sessionStorage) {
		return;
	}

	const endpoint = getPlayerLaunchDiagnosticEndpoint();
	if (!endpoint) {
		return;
	}

	const activityId = _.toString(meta.activityId || "").trim();
	const instanceId = _.toString(meta.instanceId || "").trim();
	const dedupeKey = `playerLaunchDiagnostic:${diagnosticType}:${activityId}:${instanceId}`;
	try {
		const lastSentAt = Number(window.sessionStorage.getItem(dedupeKey) || 0);
		const now = Date.now();
		if (lastSentAt && now - lastSentAt < PLAYER_LAUNCH_SERVER_DIAGNOSTIC_COOLDOWN_MS) {
			return;
		}
		window.sessionStorage.setItem(dedupeKey, String(now));
	} catch (_err) {
		// Best-effort throttle only.
	}

	const body = {
		type: "diagnostic",
		diagnosticType,
		source: "ambifi-cloud-player-launch",
		timestamp: new Date().toISOString(),
		meta: sanitizePlayerLaunchDiagnosticMeta(meta)
	};

	const diagnosticRequest = request.post(endpoint)
		.set("Content-Type", "application/json");
	const jwt = userActions.getJwtToken();
	if (jwt) {
		diagnosticRequest.set("Authorization", jwt);
	}
	diagnosticRequest.timeout({
		response: 5000,
		deadline: 8000
	})
		.send(JSON.stringify(body))
		.then(() => {
			logWebLoginSequence("player.serverDiagnostic sent", {
				diagnosticType,
				activityId,
				instanceId
			});
		})
		.catch((err) => {
			logWebLoginSequence("player.serverDiagnostic failed", {
				diagnosticType,
				activityId,
				instanceId,
				error: err && err.message ? err.message : `${err}`
			});
		});
}

function recordPlayerLaunchLoopSignal(meta = {}) {
	if (typeof window === "undefined" || !window.sessionStorage) {
		return;
	}

	const activityId = _.toString(meta.activityId || "").trim();
	const instanceId = _.toString(meta.instanceId || "").trim();
	if (!activityId || !instanceId) {
		return;
	}

	try {
		const now = Date.now();
		const existingRaw = window.sessionStorage.getItem(PLAYER_LAUNCH_LOOP_STORAGE_KEY);
		const existing = existingRaw ? JSON.parse(existingRaw) : {};
		const recentEvents = (_.isArray(existing.events) ? existing.events : [])
			.filter((event) => {
				return event &&
					event.activityId === activityId &&
					now - Number(event.at || 0) <= PLAYER_LAUNCH_LOOP_WINDOW_MS;
			});
		recentEvents.push({
			at: now,
			activityId,
			instanceId,
			source: _.toString(meta.source || ""),
			path: getCurrentLaunchPath()
		});

		const distinctInstanceIds = _.uniq(recentEvents.map((event) => event.instanceId).filter(Boolean));
		const nextState = {
			events: recentEvents.slice(-20),
			lastAlertAt: Number(existing.lastAlertAt || 0)
		};

		if (distinctInstanceIds.length >= PLAYER_LAUNCH_LOOP_DISTINCT_INSTANCE_THRESHOLD &&
			now - nextState.lastAlertAt > PLAYER_LAUNCH_LOOP_ALERT_COOLDOWN_MS) {
			const diagnosticMeta = _.assign({}, meta, {
				currentPath: getCurrentLaunchPath(),
				distinctInstanceIds,
				eventCount: recentEvents.length,
				windowMs: PLAYER_LAUNCH_LOOP_WINDOW_MS
			});
			nextState.lastAlertAt = now;
			logInAppConsole("player.launchLoop suspected", diagnosticMeta);
			postPlayerLaunchDiagnostic("launchLoopSuspected", diagnosticMeta);
			if (window.__AMBIFI_WEB_DIAGNOSTICS__ && _.isFunction(window.__AMBIFI_WEB_DIAGNOSTICS__.open)) {
				setTimeout(() => window.__AMBIFI_WEB_DIAGNOSTICS__.open("launchLoopSuspected"), 100);
			}
		}

		window.sessionStorage.setItem(PLAYER_LAUNCH_LOOP_STORAGE_KEY, JSON.stringify(nextState));
	} catch (err) {
		console.warn("Unable to record player launch loop signal", err);
	}
}

function recordPlayerLaunchDecision(label, meta = {}, options = {}) {
	const eventMeta = _.assign({
		currentPath: getCurrentLaunchPath(),
		origin: getLs("origin") || "",
		userEntryPoint: state.get(["user", "entryPoint"]) || "",
		selectedActivityId: state.get(PlayerPaths.SELECTED_ACTIVITY_ID) || "",
		selectedActivityInstanceId: state.get(PlayerPaths.SELECTED_ACTIVITY_INSTANCE_ID) || ""
	}, getLaunchUserMeta(), meta || {});

	logWebLoginSequence(label, eventMeta);
	if (options.inAppConsole === true) {
		logInAppConsole(label, eventMeta);
	}
	if (options.serverDiagnostic) {
		postPlayerLaunchDiagnostic(_.isString(options.serverDiagnostic) ? options.serverDiagnostic : label, eventMeta);
	}

	recordPlayerLaunchLoopSignal(eventMeta);
}

function getPlaylistRequestKey(playlistId) {
	if (_.isString(playlistId)) {
		return playlistId;
	}

	if (_.isNil(playlistId)) {
		return "";
	}

	return `${playlistId}`;
}

function buildReloadDebugMeta(requestKey, meta = {}) {
	return _.assign({
		playlistId: requestKey,
		statePlaylistId: getPlaylistRequestKey(state.get(["playlistId"])),
		activitiesLoaded: !!state.get(PlayerPaths.ACTIVITIES_LOADED),
		reloadActivitiesFlag: !!state.get(PlayerPaths.RELOAD_ACTIVITIES),
		showSpinner: !!state.get(PlayerPaths.SHOW_SPINNER),
		activeReloadKeys: _.keys(reloadActivitiesPromisesByKey),
		activeReloadFastKeys: _.keys(reloadActivitiesFastPromisesByKey)
	}, meta || {});
}

function getRecentActivityCacheStorageKey(tree = state) {
	const identityId = _.trim(_.get(tree.get(["user"]), "identityId", ""));
	return identityId ? `${identityId}:${RECENT_ACTIVITY_CACHE_STORAGE_SUFFIX}` : null;
}

function buildRecentActivitySnapshot(activity) {
	if (!activity || !activity.id) {
		return null;
	}

	return _.pick(activity, RECENT_ACTIVITY_SNAPSHOT_FIELDS);
}

function normalizeRecentActivityCache(recentActivityCache) {
	if (!_.isPlainObject(recentActivityCache)) {
		return {};
	}

	const normalizedRecentActivityCache = {};
	_.forEach(recentActivityCache, (activity) => {
		const snapshot = buildRecentActivitySnapshot(activity);
		if (snapshot) {
			normalizedRecentActivityCache[snapshot.id] = snapshot;
		}
	});

	const activityIds = _.keys(normalizedRecentActivityCache);
	return activityIds
		.slice(Math.max(activityIds.length - RECENT_ACTIVITY_CACHE_MAX_ENTRIES, 0))
		.reduce((cache, activityId) => {
			cache[activityId] = normalizedRecentActivityCache[activityId];
			return cache;
		}, {});
}

function persistRecentActivityCache(tree = state, recentActivityCache = tree.get(["recentActivityCache"])) {
	const storageKey = getRecentActivityCacheStorageKey(tree);
	if (!storageKey) {
		return;
	}

	try {
		setLs(storageKey, normalizeRecentActivityCache(recentActivityCache));
	} catch (err) {
		console.warn("Unable to persist recent activity cache", err);
	}
}

export function hydrateRecentActivityCache(tree = state) {
	const storageKey = getRecentActivityCacheStorageKey(tree);
	let recentActivityCache = {};

	if (storageKey) {
		try {
			const storedRecentActivityCache = getLs(storageKey);
			recentActivityCache = normalizeRecentActivityCache(storedRecentActivityCache);
			if (!_.isEqual(storedRecentActivityCache, recentActivityCache)) {
				persistRecentActivityCache(tree, recentActivityCache);
			}
		} catch (err) {
			console.warn("Unable to hydrate recent activity cache", err);
			recentActivityCache = {};
		}
	}

	tree.set(["recentActivityCache"], recentActivityCache);
	return recentActivityCache;
}

export function mergeRecentActivityCache(activities = [], tree = state) {
	if (!_.isArray(activities) || activities.length === 0) {
		return normalizeRecentActivityCache(tree.get(["recentActivityCache"]));
	}

	const currentRecentActivityCache = normalizeRecentActivityCache(tree.get(["recentActivityCache"]));
	const nextRecentActivityCache = Object.assign({}, currentRecentActivityCache);
	let hasChanges = false;

	activities.forEach((activity) => {
		const snapshot = buildRecentActivitySnapshot(activity);
		if (!snapshot) {
			return;
		}

		if (!_.isEqual(nextRecentActivityCache[snapshot.id], snapshot)) {
			nextRecentActivityCache[snapshot.id] = snapshot;
			hasChanges = true;
		}
	});

	if (hasChanges) {
		tree.set(["recentActivityCache"], nextRecentActivityCache);
		persistRecentActivityCache(tree, nextRecentActivityCache);
	}

	return hasChanges ? nextRecentActivityCache : currentRecentActivityCache;
}

export async function backfillRecentActivityCache(activityIds = [], tree = state) {
	const uniqueActivityIds = _.uniq(
		(activityIds || []).filter((activityId) => _.isString(activityId) && _.trim(activityId) !== "")
	);

	if (uniqueActivityIds.length === 0) {
		return normalizeRecentActivityCache(tree.get(["recentActivityCache"]));
	}

	const currentRecentActivityCache = normalizeRecentActivityCache(tree.get(["recentActivityCache"]));
	const missingActivityIds = uniqueActivityIds.filter((activityId) => !currentRecentActivityCache[activityId]);
	if (missingActivityIds.length === 0) {
		return currentRecentActivityCache;
	}

	try {
		await refreshCredentials();
	} catch (err) {
		console.warn("Unable to refresh credentials while backfilling recent activity cache", err);
	}

	const resolvedActivities = [];
	for (const activityId of missingActivityIds) {
		try {
			const activityMetaData = await getActivityMetadata(activityId);
			if (activityMetaData && activityMetaData.id) {
				resolvedActivities.push(activityMetaData);
			}
		} catch (err) {
			console.warn(`Unable to backfill recent activity metadata for ${activityId}`, err);
		}
	}

	if (resolvedActivities.length > 0) {
		return mergeRecentActivityCache(resolvedActivities, tree);
	}

	return normalizeRecentActivityCache(tree.get(["recentActivityCache"]));
}

function applyInitializedPlayerState(inventory, sessions = {}) {
	state.set(PlayerPaths.ACTIVITIES, inventory);
	state.set(PlayerPaths.SESSIONS, sessions);
	state.set(PlayerPaths.ACTIVITIES_LOADED, true);
	state.set(PlayerPaths.RELOAD_ACTIVITIES, false);
}

function getSessionTimestampMs(session = {}) {
	if (session.updatedDateTimeDate instanceof Date) {
		return session.updatedDateTimeDate.getTime();
	}

	if (_.isNumber(session.updatedDateTimeDate)) {
		return session.updatedDateTimeDate;
	}

	if (_.isNumber(session.lastUpdatedDateTime)) {
		return session.lastUpdatedDateTime * 1000;
	}

	if (_.isString(session.lastUpdatedDate)) {
		const parsedLastUpdatedDate = Date.parse(session.lastUpdatedDate);
		if (_.isFinite(parsedLastUpdatedDate)) {
			return parsedLastUpdatedDate;
		}
	}

	if (session.createdDateTimeDate instanceof Date) {
		return session.createdDateTimeDate.getTime();
	}

	if (_.isNumber(session.createdDateTimeDate)) {
		return session.createdDateTimeDate;
	}

	if (_.isNumber(session.createdDateTime)) {
		return session.createdDateTime * 1000;
	}

	if (_.isString(session.createdDateTimeIsoString)) {
		const parsedCreatedDate = Date.parse(session.createdDateTimeIsoString);
		if (_.isFinite(parsedCreatedDate)) {
			return parsedCreatedDate;
		}
	}

	return 0;
}

function mergeSessionLists(existingSessions = [], incomingSessions = []) {
	const mergedByInstanceKey = new Map();

	[...(Array.isArray(existingSessions) ? existingSessions : []), ...(Array.isArray(incomingSessions) ? incomingSessions : [])].forEach((session) => {
		if (!session) {
			return;
		}

		const sessionKey = _.trim(
			_.toString(session.instanceId || session.url || `${session.id || ""}:${session.createdDateTime || ""}:${session.lastUpdatedDateTime || ""}`)
		);

		if (!sessionKey) {
			return;
		}

		const current = mergedByInstanceKey.get(sessionKey);
		if (!current || getSessionTimestampMs(session) >= getSessionTimestampMs(current)) {
			mergedByInstanceKey.set(sessionKey, session);
		}
	});

	return _.orderBy(Array.from(mergedByInstanceKey.values()), [getSessionTimestampMs], ["desc"]);
}

function mergeSessionsMap(existingSessions = {}, incomingSessions = {}) {
	const nextSessions = _.isPlainObject(existingSessions) ? _.cloneDeep(existingSessions) : {};
	if (!_.isPlainObject(incomingSessions)) {
		return nextSessions;
	}

	Object.keys(incomingSessions).forEach((activityId) => {
		nextSessions[activityId] = mergeSessionLists(nextSessions[activityId], incomingSessions[activityId]);
	});

	return nextSessions;
}

export function initializePlayer(inventory) {
	const endInitializePlayer = startPerfTimer("cloud.player.initializePlayer", {
		inventoryCount: _.isArray(inventory) ? inventory.length : 0
	});
	console.log("Algolia search");
	return algolia.searchUserActivities().then((content) => {
		console.log("Algolia search (2)");
		let sessions = {};

		_.each(content.hits, (hit) => {

			if (_.isUndefined(sessions[hit.id])) {
				sessions[hit.id] = [];
			}
			sessions[hit.id].push(hit);
		});


		_.each(sessions, (activitySessions, id) => {
			sessions[id] = _.orderBy(activitySessions, ["updatedDateTimeDate"], ["desc"]);
		});
		console.log("Refresh Sessions", sessions);
		applyInitializedPlayerState(inventory, sessions);
		endInitializePlayer({
			historyHits: _.get(content, "hits.length", 0),
			sessionActivityCount: _.keys(sessions).length
		});
		return { hits: _.get(content, "hits", []), sessions };

	}).catch((err) => {
		console.error("Algolia search (2)", err);
		applyInitializedPlayerState(inventory, {});
		endInitializePlayer({
			error: err && err.message ? err.message : "Unknown error",
			fellBackToEmptySessions: true
		});
		return { hits: [], sessions: {} };
	});
}

export function loadActivities(baseInventory = null) {
	return inventoryActions.assembleActivitiesForWeb(baseInventory).then((result) => {
		state.set(PlayerPaths.ACTIVITIES, result);

		return algolia.searchUserActivities().then((content) => {
			let sessions = {};

			_.each(content.hits, (hit) => {

				if (_.isUndefined(sessions[hit.id])) {
					sessions[hit.id] = [];
				}
				sessions[hit.id].push(hit);
			});


			_.each(sessions, (activitySessions, id) => {
				sessions[id] = _.orderBy(activitySessions, ["updatedDateTimeDate"], ["desc"]);
			});

			state.set(PlayerPaths.SESSIONS, sessions);
			state.set(PlayerPaths.ACTIVITIES_LOADED, true);
			state.set(PlayerPaths.RELOAD_ACTIVITIES, false);

		}).catch((err) => {
			throw err;
		});

	}).catch((err) => {
		console.error(err);
	});

}

export function showPlayerSpinner() {
	state.set(PlayerPaths.SHOW_SPINNER, true);
	state.set(["appState", "documents", "showSpinner"], true);
}

export function hidePlayerSpinner() {
	state.set(PlayerPaths.SHOW_SPINNER, false);
	state.set(["appState", "documents", "showSpinner"], false);
}

/**
 *  Reload activities due to add/remove
 */
export function reloadActivities(refreshAssignments = false, playlistId) {
	const requestKey = getPlaylistRequestKey(playlistId);
	const requestId = `reload-${++reloadActivitiesRequestSequence}`;
	const existingPromise = reloadActivitiesPromisesByKey[requestKey];
	logInAppConsole("playlist.reloadActivities requested", buildReloadDebugMeta(requestKey, {
		requestId,
		refreshAssignments: !!refreshAssignments,
		deduped: !!existingPromise
	}));
	if (existingPromise) {
		return existingPromise;
	}

	const promise = new Promise((resolve, reject) => {
		const endReloadActivities = startPerfTimer("cloud.player.reloadActivities", {
			refreshAssignments: !!refreshAssignments,
			playlistId: requestKey
		});
		logInAppConsole("playlist.reloadActivities started", buildReloadDebugMeta(requestKey, {
			requestId,
			refreshAssignments: !!refreshAssignments
		}));
		showPlayerSpinner();

		setTimeout(() => {

			(async () => {
				try {
					await refreshCredentials();
					const endFetchInventory = startPerfTimer("cloud.player.reloadActivities.fetchInventory");
					// const inventoryRes = await request.get(`${env.apiGateway.baseUrl}${env.apiGateway.endpoints.user.inventory}`).set({ Authorization: userActions.getJwtToken() });
					// const { inventory } = inventoryRes.body;
					// const driveRes = await request.get(`${env.apiGateway.baseUrl}${env.apiGateway.endpoints.user.drive}`).set({ Authorization: userActions.getJwtToken() });
					// const { drive } = driveRes.body;

					const jwt = userActions.getJwtToken();
					const inventoryRes = await fetchInventory(userActions.getJwtToken(), playlistId);
					endFetchInventory({
						inventoryCount: _.get(inventoryRes, "inventory.length", 0)
					});
					const { inventory, workflows, workflowParticipants } = inventoryRes;
					logInAppConsole("playlist.reloadActivities inventory fetched", buildReloadDebugMeta(requestKey, {
						requestId,
						inventoryCount: _.isArray(inventory) ? inventory.length : 0
					}));
					hydrateRecentActivityCache();
					mergeRecentActivityCache(inventory);
					const endFetchDrive = startPerfTimer("cloud.player.reloadActivities.fetchDrive");
					const drive = await fetchDrive(jwt);
					endFetchDrive();
					//const inventoryRes = await request.get(`${env.apiGateway.baseUrl}${env.apiGateway.endpoints.user.inventory}?includeDrive=true`).set({ Authorization: userActions.getJwtToken() });
					//const { inventory, drive, workflows, workflowParticipants } = inventoryRes.body;

					inventoryActions.initializeInventoryV2(inventory);
					await initializePlayer(inventory);
					await initializeDrive(drive);
					await initializeWorkflows(workflows);
					await initializeWorkflowParticipants(workflowParticipants);
					if (refreshAssignments) {
						await getAssignmentScheduleForCurrentUser();
					}

					hidePlayerSpinner();
					const durationMs = endReloadActivities({
						inventoryCount: _.isArray(inventory) ? inventory.length : 0
					});
					logInAppConsole("playlist.reloadActivities finished", buildReloadDebugMeta(requestKey, {
						requestId,
						durationMs,
						inventoryCount: _.isArray(inventory) ? inventory.length : 0
					}));
					resolve();
				} catch (err) {
					hidePlayerSpinner();
					const durationMs = endReloadActivities({
						error: err && err.message ? err.message : `${err}`
					});
					logInAppConsole("playlist.reloadActivities failed", buildReloadDebugMeta(requestKey, {
						requestId,
						durationMs,
						error: err && err.message ? err.message : `${err}`
					}));
					console.error("An error occurred while attempting to fetch inventory", err);
					showError("Load Activities", "Unable to load activities, this might be due to network connectivity issues.");
					reject(err);
				}
			})();
		}, 2000);

	}).finally(() => {
		delete reloadActivitiesPromisesByKey[requestKey];
		logInAppConsole("playlist.reloadActivities cleared", buildReloadDebugMeta(requestKey, {
			requestId
		}));
	});
	reloadActivitiesPromisesByKey[requestKey] = promise;
	return promise;
	// return new Promise((resolve, reject) => {
	// 	inventoryActions.initializeInventory(true).then(async (inventory) => {
	// 		loadActivities(inventory).then(() => {
	// 			tree.set(PlayerPaths.RELOAD_ACTIVITIES, false);
	// 			hidePlayerSpinner();
	// 			resolve();
	// 		}).catch((err) => {
	// 			console.error("Load Activities", err, err.stack);
	// 			showError("Load Activities", "Unable to load activities, this might be due to network connectivity issues.");
	// 			hidePlayerSpinner();
	// 			reject(err);
	// 		});
	// 	});
	// });
}

export function reloadActivitiesFast(playlistId, options = {}) {
	const requestKey = getPlaylistRequestKey(playlistId);
	const requestId = `reload-fast-${++reloadActivitiesFastRequestSequence}`;
	const reloadOptions = _.isPlainObject(options) ? options : {};
	const showSpinner = reloadOptions.showSpinner !== false;
	const existingPromise = reloadActivitiesFastPromisesByKey[requestKey];
	logInAppConsole("playlist.reloadActivitiesFast requested", buildReloadDebugMeta(requestKey, {
		requestId,
		deduped: !!existingPromise,
		showSpinner
	}));
	if (existingPromise) {
		return existingPromise;
	}

	const promise = new Promise((resolve, reject) => {
		const endReloadActivitiesFast = startPerfTimer("cloud.player.reloadActivitiesFast", {
			playlistId: requestKey
		});
		logInAppConsole("playlist.reloadActivitiesFast started", buildReloadDebugMeta(requestKey, {
			requestId,
			showSpinner
		}));
		if (showSpinner) {
			showPlayerSpinner();
		}

		(async () => {
			try {
				await refreshCredentials();
				const endFetchInventory = startPerfTimer("cloud.player.reloadActivitiesFast.fetchInventory");
				const inventoryRes = await fetchInventory(userActions.getJwtToken(), playlistId);
				endFetchInventory({
					inventoryCount: _.get(inventoryRes, "inventory.length", 0)
				});
				const { inventory } = inventoryRes;
				logInAppConsole("playlist.reloadActivitiesFast inventory fetched", buildReloadDebugMeta(requestKey, {
					requestId,
					inventoryCount: _.isArray(inventory) ? inventory.length : 0
				}));
				hydrateRecentActivityCache();
				mergeRecentActivityCache(inventory);

				console.log("!@!@!@ INVENTORY", inventory);

				const endInitializeInventory = startPerfTimer("cloud.player.reloadActivitiesFast.initializeInventoryV2", {
					inventoryCount: _.isArray(inventory) ? inventory.length : 0
				});
				inventoryActions.initializeInventoryV2(inventory);
				endInitializeInventory();
				await initializePlayer(inventory);

				if (showSpinner) {
					hidePlayerSpinner();
				}
				const durationMs = endReloadActivitiesFast({
					inventoryCount: _.isArray(inventory) ? inventory.length : 0
				});
				logInAppConsole("playlist.reloadActivitiesFast finished", buildReloadDebugMeta(requestKey, {
					requestId,
					durationMs,
					inventoryCount: _.isArray(inventory) ? inventory.length : 0,
					showSpinner
				}));
				resolve();
			} catch (err) {
				if (showSpinner) {
					hidePlayerSpinner();
				}
				const durationMs = endReloadActivitiesFast({
					error: err && err.message ? err.message : `${err}`
				});
				logInAppConsole("playlist.reloadActivitiesFast failed", buildReloadDebugMeta(requestKey, {
					requestId,
					durationMs,
					error: err && err.message ? err.message : `${err}`,
					showSpinner
				}));
				console.error("An error occurred while attempting to fetch inventory", err);
				showError("Load Activities", "Unable to load activities, this might be due to network connectivity issues.");
				reject(err);
			}
		})();
	}).finally(() => {
		delete reloadActivitiesFastPromisesByKey[requestKey];
		logInAppConsole("playlist.reloadActivitiesFast cleared", buildReloadDebugMeta(requestKey, {
			requestId,
			showSpinner
		}));
	});
	reloadActivitiesFastPromisesByKey[requestKey] = promise;
	return promise;
	// return new Promise((resolve, reject) => {
	// 	inventoryActions.initializeInventory(true).then(async (inventory) => {
	// 		loadActivities(inventory).then(() => {
	// 			tree.set(PlayerPaths.RELOAD_ACTIVITIES, false);
	// 			hidePlayerSpinner();
	// 			resolve();
	// 		}).catch((err) => {
	// 			console.error("Load Activities", err, err.stack);
	// 			showError("Load Activities", "Unable to load activities, this might be due to network connectivity issues.");
	// 			hidePlayerSpinner();
	// 			reject(err);
	// 		});
	// 	});
	// });
}

function getFirstAssignmentForActivity(activityId) {
	let assignment = null;

	const assignmentsSchedule = state.get(["assignments", "schedule"]);

	if (_.isArray(assignmentsSchedule) && assignmentsSchedule.length > 0) {
			const findAssignments = assignmentsSchedule.filter((item) => {
				return activityId === item.activityId;
			});

		// Found at least one
		if (findAssignments.length > 0) {
				// Now filter out submitted ones
				const notSubmittedAssignments = findAssignments.filter((item) => {
					return (!item.submitted || item.submitted === "");
				});

			notSubmittedAssignments.sort((a, b) => naturalCompare(a.dueDate, b.dueDate));

				const submittedAssignments = findAssignments.filter((item) => {
					return (item.submitted && item.submitted !== "");
				});

			submittedAssignments.sort((a, b) => naturalCompare(a.dueDate, b.dueDate));
			submittedAssignments.reverse();

			// No more assigmnents left
			// if (notSubmittedAssignments.length === 0) {
			// 	navActions.navigateTo("home");

			// 	setTimeout(() => {
			// 		showInfo("Assignment", "The activity <b>" + submittedAssignments[0].activityName + "</b> was an assignment and was submitted on <b>" + new Date(submittedAssignments[0].submitted).toLocaleString() + "</b>. You have no more assignments to submit.");
			// 	}, 1000);
			// 	return;
			// }

			if (notSubmittedAssignments.length > 0) {
				// Set assignment to first one due that hasn't been completed yet 
				assignment = notSubmittedAssignments[0];
			}
		}
	}

	return assignment;
}

function getAssignmentForInstance(instanceId) {
	let assignment = null;

	const assignmentsSchedule = state.get(["assignments", "schedule"]);

	const findAssignment = assignmentsSchedule.find((item) => {
		return instanceId === item.instanceId;
	});

	if (findAssignment) {
		assignment = findAssignment;
	}

	return assignment;
}

const RESUME_HISTORY_ATTRIBUTES = [
	"objectID",
	"instanceId",
	"id",
	"activityId",
	"identityId",
	"url",
	"name",
	"sessionName",
	"displayName",
	"createdDateTime",
	"createdDateTimeIsoString",
	"lastUpdatedDateTime",
	"lastUpdatedDateTimeIsoString"
];

async function loadAuthorizedHistoryResumeSession(instanceId, assignment = null, options = {}) {
	const index = algolia.getHistoryIndex();
	const hasExactWorkItemAuthorization = options.allowExactWorkItemLookup === true &&
		!!_.toString(options.workItemId || "").trim();
	// A current assignment came from the authenticated user's schedule and is
	// itself authorization for that one preallocated instance ID.
	if (assignment || hasExactWorkItemAuthorization) {
		const response = await index.getObjects([instanceId], {
			attributesToRetrieve: RESUME_HISTORY_ATTRIBUTES
		});
		return _.get(response, "results[0]", null);
	}

	const unrestricted = canResumeAnyHistoryInCurrentOrg({
		isOrganizationAdministrator: userActions.isOrganizationAdministrator()
	});
	const filters = buildHistoryResumeFilters({
		instanceId,
		identityId: userActions.getIdentityId(),
		unrestricted
	});
	if (!filters) {
		throw new Error("The current user does not have an authorized activity-history scope.");
	}

	const response = await index.search("", {
		attributesToRetrieve: RESUME_HISTORY_ATTRIBUTES,
		filters,
		hitsPerPage: 1,
		page: 0
	});
	return _.get(response, "hits[0]", null);
}

function normalizeInitialItemId(initialItemId) {
	const value = _.toString(initialItemId || "").trim();

	if (!value) {
		return "";
	}

	return value.startsWith("#") ? value : `#${value}`;
}

function getPlayerLaunchSearch() {
	if (typeof window === "undefined" || !window.location) {
		return "";
	}

	try {
		const params = new URLSearchParams(_.toString(window.location.search || ""));
		const collab = _.toString(params.get("collab") || "").trim().toLowerCase();
		const sharedSessionUrl = _.toString(params.get("sharedSessionUrl") || "").trim();
		const instanceUrl = _.toString(params.get("instanceUrl") || params.get("activityInstanceUrl") || "").trim();
		const workItemId = _.toString(params.get("workItemId") || "").trim();
		const rawHealthStreamFutureValue = getFeatureFlagValue("hsFuture", state.get(["appState", "urlParams"]));
		const healthStreamFutureValue = rawHealthStreamFutureValue === undefined
			? ""
			: _.toString(rawHealthStreamFutureValue).trim();
		const skuPhotoMappingValue = _.toString(params.get("skuPhotoMapping") || "").trim().toLowerCase();
		const skuPhotoMapping = skuPhotoMappingValue === "1"
			|| skuPhotoMappingValue === "true"
			|| skuPhotoMappingValue === "yes";
		const workflowParamKeys = [
			"workflowReadOnly",
			"workflowOpenMode",
			"workflowReason",
			"afVars",
			"afFilters"
		];
		const hasWorkflowParams = workflowParamKeys.some((key) => _.toString(params.get(key) || "").trim() !== "");
		const isWorkflowLaunch = !!workItemId || hasWorkflowParams;
		const workflowInstanceUrl = instanceUrl || (isWorkflowLaunch ? sharedSessionUrl : "");
		const isCollaborationLaunch = !isWorkflowLaunch && sharedSessionUrl && (collab === "1" || collab === "true");
		if (!workflowInstanceUrl && !isCollaborationLaunch && !workItemId && !hasWorkflowParams && !skuPhotoMapping && !healthStreamFutureValue) {
			return "";
		}

		const preservedParams = new URLSearchParams();
		if (workflowInstanceUrl) {
			preservedParams.set("instanceUrl", workflowInstanceUrl);
		} else if (isCollaborationLaunch) {
			preservedParams.set("collab", "1");
			preservedParams.set("sharedSessionUrl", sharedSessionUrl);
		}
		if (workItemId) {
			preservedParams.set("workItemId", workItemId);
		}
		if (skuPhotoMapping) {
			preservedParams.set("skuPhotoMapping", "1");
		}
		if (healthStreamFutureValue) {
			preservedParams.set("hsFuture", healthStreamFutureValue);
		}
		workflowParamKeys.forEach((key) => {
			const value = params.get(key);
			if (_.toString(value || "").trim() !== "") {
				preservedParams.set(key, value);
			}
		});
		return `?${preservedParams.toString()}`;
	} catch (err) {
		console.warn("Unable to preserve player launch params", err);
		return "";
	}
}

function buildPlayerLocation(activityId, instanceId, initialItemId = null, freshInstanceAuthorization = null, healthStreamContext = null, launchProvenance = null) {
	const normalizedInitialItemId = normalizeInitialItemId(initialItemId);
	const locationState = {};
	if (normalizedInitialItemId) {
		locationState.initialItemId = normalizedInitialItemId;
		locationState.instanceId = instanceId;
	}
	if (isExactFreshInstanceAuthorization(freshInstanceAuthorization, activityId, instanceId)) {
		locationState.freshInstanceAuthorization = freshInstanceAuthorization;
	}
	if (launchProvenance &&
		launchProvenance.activityId === activityId &&
		launchProvenance.instanceId === instanceId) {
		locationState.activityInstanceLaunch = launchProvenance;
	}
	if (_.isPlainObject(healthStreamContext) && healthStreamContext.courseId) {
		locationState.healthStreamContext = healthStreamContext;
	}
	return {
		pathname: `/player/${activityId}/${instanceId}`,
		search: getPlayerLaunchSearch(),
		hash: normalizedInitialItemId,
		state: _.isEmpty(locationState) ? undefined : locationState
	};
}

function navigateToPlayerActivity(tree, activityId, instanceId, initialItemId = null, meta = {}) {
	const freshInstanceAuthorization = _.get(meta, "freshInstanceAuthorization", null);
	const launchProvenance = _.get(meta, "launchProvenance", null);
	const healthStreamContext = tree.get(PlayerPaths.SELECTED_ACTIVITY_HS_CONTEXT);
	const diagnosticMeta = _.omit(meta || {}, ["freshInstanceAuthorization", "launchProvenance"]);
	tree.set(PlayerPaths.SELECTED_ACTIVITY_ID, activityId);
	tree.set(PlayerPaths.SELECTED_ACTIVITY_INSTANCE_ID, instanceId);
	recordPlayerLaunchDecision("player.navigateToPlayerActivity", _.assign({
		activityId,
		instanceId,
		initialItemId: initialItemId || ""
	}, diagnosticMeta), {
		inAppConsole: !!(meta && meta.inAppConsole),
		serverDiagnostic: meta && meta.serverDiagnostic
	});
	const location = buildPlayerLocation(activityId, instanceId, initialItemId, freshInstanceAuthorization, healthStreamContext, launchProvenance);
	browserHistory.push(location);
}

function buildFreshInstanceLaunchAuthorization(activityId, instanceId, launchId = generateUUID()) {
	return buildFreshInstanceAuthorization({
		activityId,
		activityInstanceId: instanceId,
		nonce: launchId
	});
}

function setSelectedHealthStreamContext(tree, healthStreamContext = null) {
	if (_.isPlainObject(healthStreamContext) && healthStreamContext.courseId) {
		tree.set(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_ID, healthStreamContext.courseId);
		tree.set(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_VERSION, healthStreamContext.courseVersion || "");
		tree.set(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_NAME, healthStreamContext.courseName || "");
		tree.set(PlayerPaths.SELECTED_ACTIVITY_HS_CONTEXT, healthStreamContext);
		return;
	}

	tree.unset(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_ID);
	tree.unset(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_VERSION);
	tree.unset(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_NAME);
	tree.unset(PlayerPaths.SELECTED_ACTIVITY_HS_CONTEXT);
}

export async function startNewActivity(tree, activityId, initialItemId, instanceId = null, assignment = null, sessionCheck = false, hsCourseId, hsCourseVersion, hsCourseName, hsContext = null, launchOptions = null) {//eslint-disable-line
	console.info("!@#!@# startNewActivity", activityId, instanceId);
	let url = null;

	let _instanceId = instanceId ? instanceId : generateUUID();
	const generatedInstanceId = !instanceId;
	recordPlayerLaunchDecision("player.startNewActivity entered", {
		activityId,
		instanceId: _instanceId,
		providedInstanceId: instanceId || "",
		generatedInstanceId,
		initialItemId: initialItemId || "",
		sessionCheck,
		assignmentMode: assignment === null ? "lookup" : (assignment === false ? "disabled" : "provided")
	});

	if (assignment === null) {
		assignment = getFirstAssignmentForActivity(activityId);
	}
	const normalizedLaunchOptions = _.isPlainObject(launchOptions) ? launchOptions : {};
	const createLaunchOrigin = _.toString(normalizedLaunchOptions.launchOrigin || "startNew").trim();
	const createLaunchId = generateUUID();
	const authorizedInstanceId = assignment ? assignment.instanceId : _instanceId;
	const freshInstanceAuthorization = buildFreshInstanceLaunchAuthorization(
		activityId,
		authorizedInstanceId,
		createLaunchId
	);
	const buildLaunchEvidence = (intent, source, targetInstanceId, origin = createLaunchOrigin, launchId = createLaunchId) =>
		buildCloudPlayerLaunchProvenance({
			activityId,
			instanceId: targetInstanceId,
			intent,
			origin,
			source,
			launchId
		});

	// HealthStream launch data must be replaced for every launch so an unrelated
	// activity can never inherit credit from the previous HealthStream course.
	if (hsCourseId) {
		const healthStreamContext = _.assign({}, _.isPlainObject(hsContext) ? hsContext : {}, {
			courseId: hsCourseId,
			courseVersion: hsCourseVersion || "",
			courseName: hsCourseName || "",
			sourceType: _.get(hsContext, "sourceType", "assigned")
		});
		setSelectedHealthStreamContext(tree, healthStreamContext);
	} else {
		setSelectedHealthStreamContext(tree);
	}

	// Let's see if this activity is in inventory
	// If not let's auto-add it
	let activities = tree.get(["activities"]);
	activities = activities.filter((item) => {
		return !item.newDoc;
	});
	const hasActivity = activities.find((item) => {
		return item.id === activityId;
	});

	if (!hasActivity) {
		try {
			const activityMetaData = await getActivityMetadata(activityId);
			activities.push(activityMetaData);
			tree.set(PlayerPaths.ACTIVITIES, activities);
			mergeRecentActivityCache([activityMetaData], tree);
			console.info("!@#!@# SET ACTIVITIES", activities);
		} catch (err) {
			navActions.navigateTo("home");

			setTimeout(() => {
				showError("Error Loading Activity", "The activity could not be found.");
			}, 1000);

			return;
		}
	}

	if (instanceId === null && sessionCheck && !assignment) {
		// See if have at least one session
		let sessions = tree.get(PlayerPaths.SESSIONS_BY_ACTIVITY_ID(activityId));
		let sessionsInstanceId = null;
		let hasSession = false;

		console.info("Sessions", sessions);

		if (!_.isUndefined(sessions) && sessions.length >= 1) {
			hasSession = true;
			//look up URL from sessions
			console.info("Sessions 0", sessions[0]);
			url = sessions[0].url;
			console.info("url", url);
			sessionsInstanceId = sessions[0].instanceId;
			console.info("instanceId", sessionsInstanceId);
		}

		if (hasSession) {
			console.info("HERE!");
			// Prompt to resume or start new
			showYesNo("You already have a session in progress for this activity. Do you want to resume?\n\nNOTE: A new session will be created if you answer No.", "Resume Activity", (yes) => {
				if (yes) {
					_instanceId = sessionsInstanceId;
					const resumeLaunchId = generateUUID();

					navigateToPlayerActivity(tree, activityId, _instanceId, initialItemId, {
						source: "startNewActivity.sessionPrompt.resume",
						generatedInstanceId: false,
						sessionCheck,
						inAppConsole: true,
						launchProvenance: buildLaunchEvidence(
							"resume",
							"startNewActivity.sessionPrompt.resume",
							_instanceId,
							_.toString(normalizedLaunchOptions.resumeOrigin || "sessionPrompt.resume").trim(),
							resumeLaunchId
						)
					});
				} else {
					// Still figure out how to handle assignment!!!

					navigateToPlayerActivity(tree, activityId, _instanceId, initialItemId, {
						source: "startNewActivity.sessionPrompt.startNew",
						generatedInstanceId,
						sessionCheck,
						inAppConsole: true,
						freshInstanceAuthorization,
						launchProvenance: buildLaunchEvidence("create", "startNewActivity.sessionPrompt.startNew", _instanceId)
					});
				}
			});
		} else {
			if (assignment) {
				const { assignmentId } = assignment;
				console.log("Setting assignmentId", assignment, assignmentId);
				tree.set(PlayerPaths.SELECTED_ACTIVITY_ASSIGNMENT_ID, assignmentId);
			}
			navigateToPlayerActivity(tree, activityId, _instanceId, initialItemId, {
				source: "startNewActivity.noExistingSession",
				generatedInstanceId,
				sessionCheck,
				assignmentId: assignment ? assignment.assignmentId : "",
				freshInstanceAuthorization,
				launchProvenance: buildLaunchEvidence("create", "startNewActivity.noExistingSession", _instanceId)
			});

		}
	} else {
		if (assignment) {
			const { assignmentId } = assignment;
			console.log("Setting assignmentId", assignment, assignmentId);
			tree.set(PlayerPaths.SELECTED_ACTIVITY_ASSIGNMENT_ID, assignmentId);
			_instanceId = assignment.instanceId;
		}

		navigateToPlayerActivity(tree, activityId, _instanceId, initialItemId, {
			source: "startNewActivity.direct",
			generatedInstanceId,
			sessionCheck,
			assignmentId: assignment ? assignment.assignmentId : "",
			freshInstanceAuthorization,
			launchProvenance: buildLaunchEvidence("create", "startNewActivity.direct", _instanceId)
		});
	}

}

export async function resumeActivity(tree, activityId, activityInstanceId, initialItemId = null, url = null, assignment = null, sessionCheck = false, options = null) {
	console.info("!@#!@# resumeNewActivity", activityId, activityInstanceId, initialItemId, url);
	const requestedInstanceId = _.toString(activityInstanceId || "").trim();
	const resumeOptions = _.isPlainObject(options) ? options : {};
	const resumeLaunchId = generateUUID();
	const resumeLaunchOrigin = _.toString(resumeOptions.launchOrigin || (url ? "history" : "resume")).trim();
	const buildResumeLaunchEvidence = (source) => buildCloudPlayerLaunchProvenance({
		activityId,
		instanceId: requestedInstanceId,
		intent: "resume",
		origin: resumeLaunchOrigin,
		source,
		launchId: resumeLaunchId
	});
	setSelectedHealthStreamContext(tree, resumeOptions.healthStreamContext);
	const hasExactWorkItemAuthorization = resumeOptions.allowExactWorkItemLookup === true &&
		!!_.toString(resumeOptions.workItemId || "").trim();
	recordPlayerLaunchDecision("player.resumeActivity entered", {
		activityId,
		instanceId: requestedInstanceId,
		initialItemId: initialItemId || "",
		hasUrl: url !== null,
		sessionCheck,
		assignmentMode: assignment === null ? "lookup" : (assignment === false ? "disabled" : "provided")
	});

	// Need to get assignment with that instanceId
	if (assignment === null) {
		assignment = getAssignmentForInstance(activityInstanceId);
	}

	// Let's see if this activity is in inventory
	// If not let's auto-add it
	let activities = tree.get(["activities"]);
	activities = activities.filter((item) => {
		return !item.newDoc;
	});
	const hasActivity = activities.find((item) => {
		return item.id === activityId;
	});

	if (!hasActivity) {
		const activityMetaData = await getActivityMetadata(activityId);
		activities.push(activityMetaData);
		tree.set(PlayerPaths.ACTIVITIES, activities);
		mergeRecentActivityCache([activityMetaData], tree);
	}

	// Need to set this right before doing redirect using browserHistory
	//tree.set(PlayerPaths.SELECTED_ACTIVITY_ID, activityId);
	//tree.set(PlayerPaths.SELECTED_ACTIVITY_INSTANCE_ID, activityInstanceId);

	if (assignment) {
		if (assignment.submitted && assignment.submitted !== "") {
			navActions.navigateTo("home");

			setTimeout(() => {
				showInfo("Assignment", "The activity <b>" + assignment.activityName + "</b> was an assignment and was submitted on <b>" + new Date(assignment.submitted).toLocaleString() + "</b>.");
			}, 1000);
			return;
		}
	}

	const sessionsPath = PlayerPaths.SESSIONS_BY_ACTIVITY_ID(activityId);
	const sessions = tree.get(sessionsPath);
	console.info("!@#!@# SESSIONS", sessions);
	const resumeResolution = await resolveActivityResume({
		activityId,
		instanceId: requestedInstanceId,
		providedUrl: url,
		sessions,
		loadHistorySession: (instanceId) => loadAuthorizedHistoryResumeSession(instanceId, assignment, resumeOptions),
		startIfHistoryRecordMissing: hasExactWorkItemAuthorization && resumeOptions.startIfHistoryRecordMissing === true
	});

	if (resumeResolution.status === "resolved") {
		url = resumeResolution.url;
		if (resumeResolution.session) {
			tree.set(sessionsPath, mergeSessionLists(sessions, [resumeResolution.session]));
		}
		if (assignment) {
			tree.set(PlayerPaths.SELECTED_ACTIVITY_ASSIGNMENT_ID, assignment.assignmentId);
		}
		// Set this at the last possible moment for resume so player is only rendered once
		console.info("!@#!@# resuming activity", activityId, activityInstanceId, url);
		navigateToPlayerActivity(tree, activityId, activityInstanceId, initialItemId, {
			source: `resumeActivity.${resumeResolution.source}`,
			assignmentId: assignment ? assignment.assignmentId : "",
			sessionCheck,
			inAppConsole: true,
			launchProvenance: buildResumeLaunchEvidence(`resumeActivity.${resumeResolution.source}`)
		});
	} else {
		//no activity session found
		tree.set(PlayerPaths.SHOW_SPINNER, false);

		if (requestedInstanceId && resumeResolution.status === "startNew") {
			console.info(`!@#!@# Authorized Project Ops instance does not exist yet; starting preallocated instance activityId: ${activityId}, instanceId: ${activityInstanceId}`);
			recordPlayerLaunchDecision("player.resumeActivity authorized missing session; starting new", {
				activityId,
				instanceId: requestedInstanceId,
				initialItemId: initialItemId || "",
				sessionCheck,
				workItemId: resumeOptions.workItemId || "",
				source: `resumeActivity.${resumeResolution.source}`
			}, {
				inAppConsole: true
			});
			startNewActivity(tree, activityId, initialItemId, requestedInstanceId, false, sessionCheck, null, null, null, null, {
				launchOrigin: "workItem.resumeOrCreate"
			});
			return;
		}

		if (shouldDelegateMissingExactResumeToPlayer({
			allowExactPlayerRouteRecovery: resumeOptions.allowExactPlayerRouteRecovery,
			activityId,
			instanceId: requestedInstanceId,
			resumeResolution
		})) {
			console.info(`!@#!@# Exact history record is not indexed yet; delegating activityId: ${activityId}, instanceId: ${activityInstanceId} to Player's exact local recovery guard`);
			recordPlayerLaunchDecision("player.resumeActivity missing history; delegated to exact Player recovery", {
				activityId,
				instanceId: requestedInstanceId,
				initialItemId: initialItemId || "",
				source: `resumeActivity.${resumeResolution.source}`,
				sessionCheck
			}, {
				inAppConsole: true
			});
			if (assignment) {
				tree.set(PlayerPaths.SELECTED_ACTIVITY_ASSIGNMENT_ID, assignment.assignmentId);
			}
			tree.set(PlayerPaths.SELECTED_ACTIVITY_ID, activityId);
			tree.set(PlayerPaths.SELECTED_ACTIVITY_INSTANCE_ID, requestedInstanceId);
			browserHistory.replace(buildPlayerLocation(
				activityId,
				requestedInstanceId,
				initialItemId,
				null,
				tree.get(PlayerPaths.SELECTED_ACTIVITY_HS_CONTEXT),
				buildResumeLaunchEvidence(`resumeActivity.${resumeResolution.source}`)
			));
			return;
		}

		if (requestedInstanceId) {
			const lookupFailed = resumeResolution.source === "historyLookupFailed";
			console.warn(`!@#!@# Exact session not found when trying to resume activityId: ${activityId}, instanceId: ${activityInstanceId}; blocking launch to prevent duplicate data`);
			recordPlayerLaunchDecision("player.resumeActivity missing exact session; blocked", {
				activityId,
				instanceId: requestedInstanceId,
				initialItemId: initialItemId || "",
				source: `resumeActivity.${resumeResolution.source}`,
				sessionCheck,
				error: resumeResolution.error && resumeResolution.error.message
			}, {
				inAppConsole: true,
				serverDiagnostic: "missingSessionBlocked"
			});
			navActions.navigateTo("home");
			setTimeout(() => {
				showError(
					lookupFailed ? "Unable to Verify Saved Activity" : "Unable to Resume Activity",
					lookupFailed
						? "The saved activity could not be verified because the history service is temporarily unavailable. No new activity was created. Refresh Status and try again."
						: "The requested saved activity could not be located or is not available to this account. No new activity was created. Refresh Status and try again, or contact support if the job should still exist."
				);
			}, 500);
			return;
		}

		console.info(`!@#!@# Session not found when trying to resume activityId: ${activityId}, no instanceId supplied, starting new activity`);
		recordPlayerLaunchDecision("player.resumeActivity missing session without instance; starting new", {
			activityId,
			instanceId: "",
			initialItemId: initialItemId || "",
			sessionCheck,
			source: "resumeActivity.missingSession.startNew"
		}, {
			inAppConsole: true
		});
		startNewActivity(tree, activityId, initialItemId, null, false, sessionCheck, null, null, null, null, {
			launchOrigin: "resume.missingInstance"
		});

		// navActions.navigateTo("home");
	}
}

export function showSessionsModal(tree, activityId) {
	const activity = inventoryActions.getActivityById(activityId);
	if (!activity || activity.trackData !== true) {
		tree.set(PlayerPaths.SORTED_SESSIONS, []);
		tree.set(PlayerPaths.SHOW_SESSIONS_MODAL, false);
		return;
	}

	tree.set(PlayerPaths.SELECTED_ACTIVITY_ID, activityId);

	sortSessions(tree, "updatedDateTimeDate");

	tree.set(PlayerPaths.SHOW_SESSIONS_MODAL, true);
}

export function closeSessionsModal(tree) {
	tree.set(PlayerPaths.SELECTED_ACTIVITY_ID, "");
	tree.set(PlayerPaths.SHOW_SESSIONS_MODAL, false);
}

export function getSelectedActivityId() {
	return state.get(PlayerPaths.SELECTED_ACTIVITY_ID);
}

export function sortSessions(tree, sortCriteria) {
	console.log("handle sort " + sortCriteria);

	let allActivitySessions = tree.get(PlayerPaths.SESSIONS);
	let selectedActivityId = tree.get(PlayerPaths.SELECTED_ACTIVITY_ID);

	if (!_.isUndefined(allActivitySessions[selectedActivityId])) {
		const now = moment();

		let sessions = allActivitySessions[selectedActivityId];
		let instanceDefinitions = sessions.map(function (instance, j) {
			const instanceDefiniton = _.cloneDeep(instance);
			instanceDefiniton.index = j;
			instanceDefiniton.today = false;
			const rawCreatedDate = instance.hasOwnProperty("createdDateTime") ? moment.utc(instance.createdDateTime * 1000).local() : null;
			let createdDateTime = null;
			if (rawCreatedDate) {
				createdDateTime = rawCreatedDate.local().format("MMMM Do YYYY, h: mm: ss a");
				instanceDefiniton.rawCreatedDate = rawCreatedDate;
				instanceDefiniton.createdDateTimeDate = rawCreatedDate.toDate();
			}
			instanceDefiniton.createdDateTimeFormatted = createdDateTime;
			const rawUpdatedDate = instance.hasOwnProperty("lastUpdatedDateTime") ? moment.utc(instance.lastUpdatedDateTime * 1000).local() : null;
			let updatedDateTime = null;
			if (rawUpdatedDate) {
				updatedDateTime = rawUpdatedDate.local().format("MMMM Do YYYY, h: mm: ss a");
				instanceDefiniton.today = now.isSame(rawUpdatedDate, "day");
				instanceDefiniton.updatedDateTimeDate = rawUpdatedDate.toDate();
			}
			instanceDefiniton.updatedDateTimeFormatted = updatedDateTime;
			return instanceDefiniton;
		});

		let sortedInstanceDefinitions = [];
		instanceDefinitions.forEach(instanceDefinition => {
			if (instanceDefinition.hasOwnProperty(sortCriteria) && instanceDefinition[sortCriteria] !== null) {
				sortedInstanceDefinitions.push(instanceDefinition);
			}
		});

		sortedInstanceDefinitions = _.reverse(_.sortBy(sortedInstanceDefinitions, [sortCriteria]));
		//console.log("SORTED", sortCriteria, sortedInstanceDefinitions);

		_.reverse(instanceDefinitions).forEach(instanceDefinition => {
			if (!instanceDefinition.hasOwnProperty(sortCriteria) || instanceDefinition[sortCriteria] === null) {
				sortedInstanceDefinitions.push(instanceDefinition);
			}
		});

		tree.set(PlayerPaths.SORTED_SESSIONS, sortedInstanceDefinitions);
	} else {
		tree.set(PlayerPaths.SORTED_SESSIONS, []);
	}
}

export function setSelectedHomeTab(tree, tab) {
	tree.set(PlayerPaths.SELECTED_HOME_TAB, tab);
}

function consumeSelectedActivityReturnPath(tree) {
	const returnPath = _.toString(tree.get(PlayerPaths.SELECTED_ACTIVITY_RETURN_PATH) || "").trim();
	tree.unset(PlayerPaths.SELECTED_ACTIVITY_RETURN_PATH);

	if (
		returnPath.indexOf("/searchV2") === 0 ||
		returnPath.indexOf("/playlistsSearchV2") === 0
	) {
		return returnPath;
	}

	return "";
}

function isWebPlayerExitSubmitted(event = {}) {
	return _.get(event, ["submitInfo", "submit"]) === true
		|| _.get(event, ["submitInfo", "completed"]) === true
		|| _.get(event, ["activity", "completionState"]) === "c"
		|| !!_.get(event, ["activity", "submitted"]);
}

function getWorkflowLaunchActivityVars(workflowLaunch = {}) {
	if (_.isPlainObject(workflowLaunch.activityVars)) {
		return workflowLaunch.activityVars;
	}
	if (_.isPlainObject(_.get(workflowLaunch, ["launchContext", "activityVars"]))) {
		return _.get(workflowLaunch, ["launchContext", "activityVars"]);
	}
	return {};
}

function getWorkflowLaunchRole(workflowLaunch = {}) {
	const activityVars = getWorkflowLaunchActivityVars(workflowLaunch);
	return _.toString(
		workflowLaunch.activityOpsRole
		|| workflowLaunch.role
		|| workflowLaunch.recipientRole
		|| activityVars.activityOpsRole
		|| activityVars.recipientRole
		|| activityVars.workItemRole
		|| activityVars.role
		|| ""
	).trim();
}

function getWebPlayerExitInstanceUrl(event = {}) {
	return _.toString(
		_.get(event, ["activity", "instanceUrl"])
		|| _.get(event, ["activity", "url"])
		|| _.get(event, ["activity", "sharedSessionUrl"])
		|| _.get(event, ["workflowLaunch", "instanceUrl"])
		|| _.get(event, ["workflowLaunch", "sharedSessionUrl"])
		|| ""
	).trim();
}

function hasManagedPricingContractProto(pricingContractProto) {
	return Boolean(pricingContractProto && _.isArray(pricingContractProto.documents) && pricingContractProto.documents.length > 0);
}

function normalizeProjectContractAnchor(anchor = null) {
	if (!_.isPlainObject(anchor)) {
		return null;
	}
	const normalized = {
		projectId: _.toString(anchor.projectId || "").trim(),
		projectName: _.toString(anchor.projectName || "").trim(),
		workItemId: _.toString(anchor.workItemId || "").trim(),
		activityId: _.toString(anchor.activityId || "").trim(),
		activityInstanceId: _.toString(anchor.activityInstanceId || anchor.instanceId || "").trim(),
		instanceUrl: _.toString(anchor.instanceUrl || anchor.activityInstanceUrl || anchor.sharedSessionUrl || anchor.url || "").trim(),
		stageKey: _.toString(anchor.stageKey || "").trim(),
		stageLabel: _.toString(anchor.stageLabel || "").trim(),
		source: _.toString(anchor.source || "project_ops_contract_anchor").trim()
	};
	return (normalized.workItemId || normalized.instanceUrl || normalized.activityInstanceId)
		? _.pickBy(normalized, value => value !== "")
		: null;
}

function getProjectContractAnchorFromEvent(event = {}) {
	const workflowLaunch = _.isPlainObject(event.workflowLaunch) ? event.workflowLaunch : {};
	const activity = _.isPlainObject(event.activity) ? event.activity : {};
	const projectOps = getProjectOpsFromEvent(event);
	const candidates = [
		event.projectContractAnchor,
		workflowLaunch.projectContractAnchor,
		activity.projectContractAnchor,
		_.get(event, ["launchContext", "projectContractAnchor"]),
		_.get(projectOps, "projectContractAnchor"),
		_.get(workflowLaunch, ["launchContext", "projectContractAnchor"])
	];
	return candidates.map(candidate => normalizeProjectContractAnchor(candidate)).find(Boolean) || null;
}

function getProjectOpsFromEvent(event = {}) {
	const workflowLaunch = _.isPlainObject(event.workflowLaunch) ? event.workflowLaunch : {};
	const activity = _.isPlainObject(event.activity) ? event.activity : {};
	return event.projectOps
		|| workflowLaunch.projectOps
		|| activity.projectOps
		|| _.get(event, ["launchContext", "projectOps"])
		|| _.get(workflowLaunch, ["launchContext", "projectOps"])
		|| {};
}

function getPricingContractProtoFromEvent(event = {}) {
	const workflowLaunch = _.isPlainObject(event.workflowLaunch) ? event.workflowLaunch : {};
	const activity = _.isPlainObject(event.activity) ? event.activity : {};
	return event.pricingContractProto
		|| workflowLaunch.pricingContractProto
		|| activity.pricingContractProto
		|| _.get(event, ["launchContext", "pricingContractProto"])
		|| _.get(workflowLaunch, ["launchContext", "pricingContractProto"])
		|| null;
}

function buildAnchorInstanceProjectOps(anchor = {}, existingProjectOps = {}, contributingProjectOps = {}) {
	return _.pickBy({
		...(existingProjectOps || {}),
		projectId: anchor.projectId || existingProjectOps.projectId || contributingProjectOps.projectId || "",
		projectName: anchor.projectName || existingProjectOps.projectName || contributingProjectOps.projectName || "",
		projectType: existingProjectOps.projectType || contributingProjectOps.projectType || "",
		projectStatus: existingProjectOps.projectStatus || contributingProjectOps.projectStatus || "",
		stageKey: existingProjectOps.stageKey || anchor.stageKey || "",
		stageLabel: existingProjectOps.stageLabel || anchor.stageLabel || "",
		activityRole: existingProjectOps.activityRole || "",
		artifactType: existingProjectOps.artifactType || "contract",
		contractMode: existingProjectOps.contractMode || "initial_purchase",
		projectContractAnchor: anchor,
		latestContribution: _.pickBy({
			stageKey: contributingProjectOps.stageKey,
			stageLabel: contributingProjectOps.stageLabel,
			addendumNumber: contributingProjectOps.addendumNumber,
			contractMode: contributingProjectOps.contractMode,
			artifactType: contributingProjectOps.artifactType
		}, value => value !== undefined && value !== null && value !== "")
	}, value => value !== undefined && value !== null && value !== "");
}

function normalizeProjectOpsTagText(value) {
	return _.toString(value || "").trim();
}

function mergeProjectOpsTags(existingTags = [], projectOps = {}) {
	const sourceTags = _.isArray(existingTags) ? existingTags : [];
	const projectName = normalizeProjectOpsTagText(projectOps.projectName);
	const projectId = normalizeProjectOpsTagText(projectOps.projectId);
	const stageLabel = normalizeProjectOpsTagText(projectOps.stageLabel || projectOps.stageKey);
	const anchor = projectOps.projectContractAnchor || {};
	const nextTags = sourceTags.filter(tag => {
		const text = normalizeProjectOpsTagText(tag).toLowerCase();
		return text !== "project-ops"
			&& text.indexOf("project:") !== 0
			&& text.indexOf("stage:") !== 0
			&& text !== "contract-anchor"
			&& text !== "contract-contributor";
	});
	if (projectName || projectId) {
		nextTags.push("project-ops");
		nextTags.push(`project: ${projectName || projectId}`);
	}
	if (stageLabel) {
		nextTags.push(`stage: ${stageLabel}`);
	}
	if (anchor && anchor.workItemId) {
		nextTags.push(anchor.stageKey === projectOps.stageKey ? "contract-anchor" : "contract-contributor");
	}
	return Array.from(new Set(nextTags));
}

async function syncProjectContractAnchorFromWebPlayerExit(event = {}) {
	if (!isProjectOpsEnabledForOrg(getOrgId())) {
		return null;
	}

	const pricingContractProto = getPricingContractProtoFromEvent(event);
	if (!hasManagedPricingContractProto(pricingContractProto)) {
		return null;
	}

	const anchor = getProjectContractAnchorFromEvent(event);
	if (!anchor || !anchor.instanceUrl) {
		return null;
	}

	const contributingProjectOps = getProjectOpsFromEvent(event);
	const currentInstanceUrl = getWebPlayerExitInstanceUrl(event);
	if (currentInstanceUrl && currentInstanceUrl === anchor.instanceUrl) {
		return null;
	}

	try {
		const anchorInstance = await awsS3.getObject(env.s3.contentBucket, anchor.instanceUrl) || {};
		const anchorProjectOps = buildAnchorInstanceProjectOps(anchor, anchorInstance.projectOps || {}, contributingProjectOps || {});
		// ICH only: a submitted contributor session (for example plumbing) stamps its
		// milestone on the home's anchor; other orgs get the instance back untouched.
		const contributingActivity = _.isPlainObject(event.activity) ? event.activity : {};
		const contributingLaunch = _.isPlainObject(event.workflowLaunch) ? event.workflowLaunch : {};
		const anchorStamp = stampIchInstanceForSave({
			...anchorInstance,
			pricingContractProto,
			projectOps: anchorProjectOps,
			projectContractAnchor: anchor,
			projectOpsProjectId: anchorProjectOps.projectId || anchorInstance.projectOpsProjectId || "",
			projectOpsProjectName: anchorProjectOps.projectName || anchorInstance.projectOpsProjectName || "",
			projectOpsStageKey: anchorProjectOps.stageKey || anchorInstance.projectOpsStageKey || "",
			projectOpsStageLabel: anchorProjectOps.stageLabel || anchorInstance.projectOpsStageLabel || "",
			tags: mergeProjectOpsTags(anchorInstance.tags || [], anchorProjectOps)
		}, {
			submitted: isWebPlayerExitSubmitted(event),
			activityId: contributingActivity.id || contributingLaunch.activityId || "",
			activityName: contributingActivity.name || contributingLaunch.activityName || ""
		});
		const nextAnchorInstance = anchorStamp.instance;
		await awsS3.saveInstance(env.s3.contentBucket, anchor.instanceUrl, nextAnchorInstance);
		if (anchor.workItemId) {
			await updateActivityWorkflowWorkItemProgress(anchor.workItemId, {
				reason: "Project contract anchor updated from contributing activity exit.",
				activityId: anchor.activityId || null,
				activityInstanceId: anchor.activityInstanceId || null,
				instanceUrl: anchor.instanceUrl,
				instanceName: anchor.projectName || null,
				pricingContractProto,
				projectOps: anchorProjectOps,
				projectContractAnchor: anchor,
				activityOps: {
					source: "cloudWebPlayerProjectContractAnchorSync",
					projectOps: anchorProjectOps,
					pricingContractProto,
					projectContractAnchor: anchor,
					contributingProjectOps
				}
			});
		}
		if (anchorStamp.reachedMilestoneKeys.length > 0) {
			await advanceIchProjectForMilestones(nextAnchorInstance, anchorStamp.reachedMilestoneKeys, { projectId: anchor.projectId });
		}
		return nextAnchorInstance;
	} catch (err) {
		console.warn("Project contract anchor sync failed", {
			anchor,
			error: err && err.message ? err.message : err
		});
		showWarningToast("Activity saved, but the project contract anchor was not updated. Reopen Status after refresh and verify the contract before continuing.");
		return null;
	}
}

async function completeWorkflowWorkItemFromWebPlayerExit(tree, event = {}) {
	const workflowLaunch = _.isPlainObject(event.workflowLaunch) ? event.workflowLaunch : {};
	const workItemId = _.toString(event.workItemId || workflowLaunch.workItemId || "").trim();
	const submitted = isWebPlayerExitSubmitted(event);
	const activity = _.isPlainObject(event.activity) ? event.activity : {};
	const activityId = _.toString(activity.id || workflowLaunch.activityId || tree.get(PlayerPaths.SELECTED_ACTIVITY_ID) || "").trim();
	const activityInstanceId = _.toString(activity.instanceId || workflowLaunch.activityInstanceId || tree.get(PlayerPaths.SELECTED_ACTIVITY_INSTANCE_ID) || "").trim();
	const activityOpsRole = getWorkflowLaunchRole(workflowLaunch);
	const projectOps = getProjectOpsFromEvent(event);
	const pricingContractProto = getPricingContractProtoFromEvent(event);
	const projectContractAnchor = getProjectContractAnchorFromEvent(event);

	console.info("Activity Ops cloud web player exit evaluated", {
		workItemId,
		submitted,
		activityId,
		activityInstanceId,
		completionState: activity.completionState || "",
		activityOpsRole,
		hasWorkflowLaunch: !_.isEmpty(workflowLaunch)
	});

	if (!workItemId) {
		return null;
	}

	const nextStatus = submitted ? "completed" : "in_progress";
	const payload = {
		status: nextStatus,
		reason: submitted
			? "Activity submitted from AmbiFi Cloud embedded web player."
			: "Activity progress saved from AmbiFi Cloud embedded web player.",
		activityId: activityId || null,
		activityInstanceId: activityInstanceId || null,
		instanceUrl: getWebPlayerExitInstanceUrl(event),
		instanceName: activity.sessionName || activity.name || null,
		instanceOpenDate: _.get(activity, ["history", 0, "timestamp"]) || null,
		instanceLastUpdatedDate: _.isArray(activity.history) && activity.history.length > 0
			? _.get(activity.history[activity.history.length - 1], "timestamp")
			: null,
		completionState: submitted ? "c" : (activity.completionState || ""),
		activityOpsRole,
		recipientRole: activityOpsRole,
		projectOps,
		pricingContractProto,
		projectContractAnchor,
		activityOps: {
			role: activityOpsRole,
			source: "cloudWebPlayerExit",
			projectOps,
			pricingContractProto,
			projectContractAnchor
		},
		workflowLaunch: _.pick(workflowLaunch, [
			"workItemId",
			"activityId",
			"activityInstanceId",
			"role",
			"activityOpsRole",
			"recipientRole",
			"recipientType",
			"status",
			"openMode",
			"projectContractAnchor"
		])
	};
	payload.workflowLaunch.projectContractAnchor = projectContractAnchor || payload.workflowLaunch.projectContractAnchor;

	try {
		const result = await updateActivityWorkflowWorkItemProgress(workItemId, payload);
		console.info("Activity Ops cloud web player exit updated work item", {
			workItemId,
			activityId,
			activityInstanceId,
			status: nextStatus,
			activityOpsRole
		});
		return result;
	} catch (err) {
		console.warn("Activity Ops cloud web player exit completion failed", {
			workItemId,
			activityId,
			activityInstanceId,
			error: err && err.message ? err.message : `${err}`
		});
		return null;
	}
}

export async function handleWebPlayerExit(tree, event) {
	event = _.isPlainObject(event) ? event : {};
	// console.log("!!!!!! EXIT", event);
	// If course is complete, then send Learning Event
	// We need the id34, course id, course identifier, course name, score

	const activityId = tree.get(PlayerPaths.SELECTED_ACTIVITY_ID);
	const healthStreamFutureEnabled = isHealthStreamFutureEnabled(tree.get(["appState", "urlParams"]));

	// Test for sending learning event to HealthStream
	try {
		if (event.hasOwnProperty("activity") && event.activity.completionState === "c") {
			const user = tree.get(["user"]) || {};

			const upn = _.toString(user["custom:upn"] || "").trim();
			const id34 = _.toString(
				healthStreamFutureEnabled
					? (user["custom:id34"] || (upn ? upn.split("@")[0] : ""))
					: (upn ? upn.split("@")[0] : "")
			).trim();
			if (id34) {

				const completedActivityInstanceId = resolveCompletedActivityInstanceId(
					event,
					tree.get(PlayerPaths.SELECTED_ACTIVITY_INSTANCE_ID)
				);
				const selectedActivityHsCourseId = tree.get(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_ID);
				const selectedActivityHsCourseVersion = tree.get(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_VERSION);
				const selectedActivityHsCourseName = tree.get(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_NAME);
				const selectedActivityHsContext = _.assign({}, tree.get(PlayerPaths.SELECTED_ACTIVITY_HS_CONTEXT) || {}, {
					activityInstanceId: completedActivityInstanceId,
					courseId: selectedActivityHsCourseId,
					courseVersion: selectedActivityHsCourseVersion,
					courseName: selectedActivityHsCourseName
				});

				if (selectedActivityHsCourseId && selectedActivityHsCourseId !== "") {
					const healthStreamScore = healthStreamFutureEnabled && selectedActivityHsContext.sourceType === "self-enrolled" ? undefined : 100;
					const resLearningEvent = await sendLearningEvent(
						id34,
						"",
						activityId,
						selectedActivityHsCourseName,
						selectedActivityHsCourseId,
						selectedActivityHsCourseVersion,
						"",
						healthStreamScore,
						healthStreamFutureEnabled ? selectedActivityHsContext : {}
					);
					console.log("POST LEARNING EVENT", resLearningEvent);

					// Refresh
					if (healthStreamFutureEnabled) {
						refreshHealthStreamLearningForCurrentUser(tree).catch(err => {
							console.error("Unable to refresh HealthStream learning after completion", err);
						});
					} else {
						getHealthStreamAssignmentScheduleForCurrentUser().then(assignments => {
							tree.set(["assignmentsHealthStream"], assignments);
						}).catch(err => {
							console.error("Unable to refresh HealthStream assignments after completion", err);
						});
					}
				} else if (healthStreamFutureEnabled && getOrgId() === HCA_HEALTHSTREAM_ORG_ID) {
					const activityInstanceId = completedActivityInstanceId;
					if (!activityId || !activityInstanceId) {
						console.warn("Skipping automatic HealthStream credit because the completed AmbiFi activity instance could not be identified", {
							activityId,
							activityInstanceId
						});
					} else {
						try {
							const creditResult = await sendLearningEventForAmbifiActivity(activityId, activityInstanceId);
							console.info("HealthStream credit-by-activity result", creditResult);
							if (_.get(creditResult, "creditedCourseCount", 0) > 0) {
								refreshHealthStreamLearningForCurrentUser(tree).catch(err => {
									console.error("Unable to refresh HealthStream learning after automatic credit", err);
								});
							}
						} catch (err) {
							// Automatic HealthStream credit must not block a completed AmbiFi activity.
							console.warn("Unable to automatically submit HealthStream credit for AmbiFi activity", {
								activityId,
								activityInstanceId,
								error: err && err.message ? err.message : `${err}`
							});
						}
					}
				}
			}
		}
	} catch (err) {
		alert("There was a problem posting a learning event to HealthStream.\n\n" + err.message);
	}

	await completeWorkflowWorkItemFromWebPlayerExit(tree, event);
	await syncProjectContractAnchorFromWebPlayerExit(event);

	try {
		if (event && event.submitInfo && event.submitInfo.submit) {
			const assignmentId = tree.get(PlayerPaths.SELECTED_ACTIVITY_ASSIGNMENT_ID);
			const instanceId = tree.get(PlayerPaths.SELECTED_ACTIVITY_INSTANCE_ID);
			const { submitted } = event.submitInfo;
			console.log("Submitting assignment", activityId, assignmentId, instanceId, submitted);
			addActivityInstance(activityId, {
				id: instanceId,
				lastUpdatedDate: getUtcTimestamp().replace(" ", "T"),
				sync: true,
				submitted
			}, true);
			markAssignmentSubmitted(assignmentId, instanceId, submitted);
		}

		// If editing in Status Detail then close dialog
		if (event.statusDetailSave) {
			closeDetailedStatusModal(tree);
		} else {
			// await reloadActivities();
			if (event.sessions) {
				tree.set(
					PlayerPaths.SESSIONS,
					mergeSessionsMap(tree.get(PlayerPaths.SESSIONS), event.sessions)
				);
			}
			tree.set(PlayerPaths.SHOW_SESSIONS_MODAL, false);
			const returnPath = consumeSelectedActivityReturnPath(tree);
			if (returnPath) {
				tree.set(["appState", "navPanel", "selected"], "search");
				browserHistory.push(returnPath);
			} else {
				navActions.navigateTo("home");
			}
		}

	} catch (e) {
		console.error("Failed to handle web player exit", e);
	} finally {
		clearSelectedActivityState();
	}
}

export async function getActivityMetadata(activityId) {
	const activityMetaData = await request.get(`${env.apiGateway.baseUrl}${env.apiGateway.endpoints.artifacts.getActivityMetadata(activityId)}`).set({ Authorization: userActions.getJwtToken() });
	return JSON.parse(activityMetaData.body.metaData);
}

export function clearSelectedActivityState() {
	state.unset(PlayerPaths.SELECTED_ACTIVITY_ID);
	state.unset(PlayerPaths.SELECTED_ACTIVITY_INSTANCE_ID);
	state.unset(PlayerPaths.SELECTED_ACTIVITY_ASSIGNMENT_ID);
	state.unset(PlayerPaths.SELECTED_ACTIVITY_RETURN_PATH);
	state.unset(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_ID);
	state.unset(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_VERSION);
	state.unset(PlayerPaths.SELECTED_ACTIVITY_HS_COURSE_NAME);
	state.unset(PlayerPaths.SELECTED_ACTIVITY_HS_CONTEXT);
}
